import React, { useEffect, useRef, useState } from "react";
import { Label } from "./ui/label";
import { Slider } from "./ui/slider";
import { Button } from "./ui/button";
import { RotateCw, Maximize2, MapPin } from "lucide-react";

/**
 * VehiclePlacer — interactive placement of the resident's vehicle on the estate map.
 *
 * Props:
 * - mapBlobUrl  : string – the estate layout map image URL.
 * - anchor      : {coords_x, coords_y} – optional starting anchor (typically the picked location).
 * - value       : {x, y, width_pct, height_pct, rotation_deg} | null
 * - onChange    : (placement | null) => void
 */
export default function VehiclePlacer({ mapBlobUrl, anchor, value, onChange }) {
    const containerRef = useRef(null);
    const [drag, setDrag] = useState(false);
    const initial = {
        x: anchor?.coords_x ?? 50,
        y: anchor?.coords_y ?? 50,
        width_pct: 4,
        height_pct: 8,
        rotation_deg: 0,
    };
    const placement = value || initial;
    const set = (patch) => onChange?.({ ...placement, ...patch });

    useEffect(() => {
        // If anchor changes and there is no explicit placement, snap to anchor.
        if (!value && anchor) {
            onChange?.({ ...initial, x: anchor.coords_x, y: anchor.coords_y });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [anchor?.coords_x, anchor?.coords_y]);

    const onPointerDown = (e) => {
        e.preventDefault();
        setDrag(true);
        e.currentTarget.setPointerCapture?.(e.pointerId);
    };
    const onPointerMove = (e) => {
        if (!drag || !containerRef.current) return;
        const rect = containerRef.current.getBoundingClientRect();
        const x = Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100));
        const y = Math.max(0, Math.min(100, ((e.clientY - rect.top) / rect.height) * 100));
        set({ x, y });
    };
    const onPointerUp = (e) => {
        setDrag(false);
        e.currentTarget.releasePointerCapture?.(e.pointerId);
    };

    if (!mapBlobUrl) {
        return (
            <div className="border border-dashed border-slate-200 rounded-lg bg-slate-50 px-4 py-3 text-xs text-slate-500" data-testid="vehicle-placer-empty">
                Vehicle placement is available once the estate map has been uploaded by an admin.
            </div>
        );
    }

    return (
        <div className="space-y-3" data-testid="vehicle-placer">
            <div
                ref={containerRef}
                className="relative border border-slate-200 rounded-lg bg-white overflow-hidden touch-none select-none"
                data-testid="vehicle-placer-map"
            >
                <img src={mapBlobUrl} alt="Estate layout" className="w-full h-auto block" draggable={false} />

                {/* Pin showing the picked location */}
                {anchor?.coords_x != null && (
                    <div
                        className="absolute -translate-x-1/2 -translate-y-full text-blue-600 pointer-events-none"
                        style={{ left: `${anchor.coords_x}%`, top: `${anchor.coords_y}%` }}
                        data-testid="vehicle-placer-anchor-pin"
                    >
                        <MapPin size={18} fill="white" strokeWidth={2.25} />
                    </div>
                )}

                {/* Draggable vehicle rectangle (centred on placement.x/y, rotated by rotation_deg). */}
                <div
                    onPointerDown={onPointerDown}
                    onPointerMove={onPointerMove}
                    onPointerUp={onPointerUp}
                    onPointerCancel={onPointerUp}
                    className="absolute cursor-move"
                    style={{
                        left: `${placement.x}%`,
                        top: `${placement.y}%`,
                        width: `${placement.width_pct}%`,
                        height: `${placement.height_pct}%`,
                        transform: `translate(-50%, -50%) rotate(${placement.rotation_deg}deg)`,
                        transformOrigin: "center center",
                    }}
                    data-testid="vehicle-shape"
                >
                    {/* Stylised car: dark slate body with a lighter "windscreen" strip at the top. */}
                    <div className="w-full h-full rounded-[14%] border-2 border-slate-900 bg-slate-700 shadow-md relative">
                        <div className="absolute top-[10%] left-[12%] right-[12%] h-[20%] bg-slate-200/80 rounded-sm" />
                        <div className="absolute top-[35%] left-[15%] right-[15%] h-[2%] bg-slate-900/60" />
                        <div className="absolute bottom-[10%] left-[12%] right-[12%] h-[15%] bg-slate-200/50 rounded-sm" />
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                    <Label className="flex items-center gap-1.5"><RotateCw size={12} /> Vehicle orientation ({Math.round(placement.rotation_deg)}°)</Label>
                    <Slider
                        min={0}
                        max={359}
                        step={1}
                        value={[placement.rotation_deg]}
                        onValueChange={(v) => set({ rotation_deg: v[0] })}
                        data-testid="vehicle-rotation-slider"
                    />
                    <div className="flex gap-1 text-[10px] uppercase tracking-widest text-slate-500">
                        {[0, 45, 90, 135, 180, 225, 270, 315].map((d) => (
                            <button
                                key={d}
                                type="button"
                                onClick={() => set({ rotation_deg: d })}
                                className="px-1.5 py-0.5 border border-slate-200 rounded hover:bg-slate-50"
                                data-testid={`rotation-preset-${d}`}
                            >
                                {d}°
                            </button>
                        ))}
                    </div>
                </div>
                <div className="space-y-2">
                    <Label className="flex items-center gap-1.5"><Maximize2 size={12} /> Vehicle size ({placement.width_pct.toFixed(1)}% wide)</Label>
                    <Slider
                        min={1.5}
                        max={15}
                        step={0.5}
                        value={[placement.width_pct]}
                        onValueChange={(v) => set({ width_pct: v[0], height_pct: v[0] * 2 })}
                        data-testid="vehicle-size-slider"
                    />
                    <div className="text-[11px] text-slate-500">
                        Aspect ratio locked at 2:1 (length:width) — common for passenger cars. Tap the car and drag to reposition.
                    </div>
                </div>
            </div>

            <div className="flex items-center justify-between text-xs text-slate-500">
                <div data-testid="vehicle-placement-readout">
                    Position: {placement.x.toFixed(1)}%, {placement.y.toFixed(1)}% · Orientation: {Math.round(placement.rotation_deg)}°
                </div>
                <Button
                    type="button"
                    variant="outline"
                    onClick={() => onChange?.(null)}
                    className="h-7 text-xs"
                    data-testid="clear-vehicle-placement"
                >
                    Clear placement
                </Button>
            </div>
        </div>
    );
}
